/**
 * SignalsTab Component
 *
 * Combines signals and opportunities components for the EventDetail page.
 * Displays recommended action, active signals, and betting opportunities.
 */

import RecommendedAction from '../components/RecommendedAction';
import SignalsSection from '../components/SignalsSection';
import OpportunitiesSection from '../components/OpportunitiesSection';
import type { Signal, Opportunity } from '../../../types';

interface SignalsTabProps {
  signals: Signal[];
  opportunities: Opportunity[];
  bestOpportunity: Opportunity | null;
}

export default function SignalsTab({
  signals,
  opportunities,
  bestOpportunity,
}: SignalsTabProps) {
  const hasSignals = signals.length > 0;
  const hasOpportunities = opportunities.length > 0;
  const showRecommendedAction = bestOpportunity && bestOpportunity.adjustedEdge >= 0.03;

  if (!hasSignals && !hasOpportunities) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-400">No active signals or opportunities</p>
        <p className="text-gray-500 text-sm mt-2">
          Signals will appear when RLM patterns are detected for this event
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Recommended Action (if best opportunity has edge >= 3%) */}
      {showRecommendedAction && (
        <RecommendedAction bestOpportunity={bestOpportunity} signals={signals} />
      )}

      {/* Two-column grid: Signals and Opportunities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Signals Section */}
        <SignalsSection signals={signals} />

        {/* Opportunities Section */}
        <OpportunitiesSection opportunities={opportunities} />
      </div>
    </div>
  );
}
